import type { FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "@privacy/translations";
import type { Answer, DocumentReference, QuestionCard } from "../../../../dynamicForm";
import { DynamicForm } from "../../../../dynamicForm";
import { EmptyPlaceholderText } from "../../../../dynamicForm/inlineForm/EmptyPlaceholderText";
import { DynamicShared } from "../../../../dynamicShared";
import { FileTextIcon, LockIcon } from "../../../../shared/icons";
import { OLD_DPA_LINK_QUESTION_ID } from "./constants";

type LinkValue = Exclude<DocumentReference, { kind: "file" }>;

const ADD_DIALOG_TRIGGER_VERTICAL_PADDING = "7.5px";

const isLinkWithUrl = (reference: DocumentReference): reference is LinkValue =>
  reference.kind !== "file" && Boolean(reference.url?.trim());

const getOldDpaLinks = (answers: Array<Answer>): Array<LinkValue> => {
  const answer = answers.find(candidate => candidate.questionId === OLD_DPA_LINK_QUESTION_ID);
  if (answer?.type !== "multipleExternalLinkInput" || !Array.isArray(answer.value)) {
    return [];
  }
  const linksWithUrl = answer.value.filter(isLinkWithUrl);
  const isFirstOccurrence = (link: LinkValue, index: number) =>
    linksWithUrl.findIndex(other => other.url === link.url && other.label === link.label) === index;
  return linksWithUrl.filter(isFirstOccurrence);
};

type OldDpaLinksFieldProps = { questionCard: QuestionCard; answers: Array<Answer> };

export const OldDpaLinksField: FC<OldDpaLinksFieldProps> = ({ questionCard, answers }) => {
  const { t } = useTranslation();
  const links = getOldDpaLinks(answers);
  const lockedLabel = t("System.SuppliersAgreements.OldLinks.Locked");
  const { header, questionText, helpInformation, extraHelpInformation } = questionCard;
  return (
    <DynamicForm.InlineFieldPrimary
      label={header}
      infoPopoverContent={{ title: questionText, body: helpInformation, info: extraHelpInformation }}
      input={
        <Stack role="group" aria-label={header} sx={{ width: "100%" }}>
          {links.length === 0 && (
            <Box sx={{ px: 2, py: ADD_DIALOG_TRIGGER_VERTICAL_PADDING }}>
              <EmptyPlaceholderText />
            </Box>
          )}
          {links.map(link => (
            <Stack
              key={`${link.label}-${link.url}`}
              direction="row"
              sx={{ alignItems: "center", gap: 1, px: 2, minHeight: 32 }}
            >
              <DynamicShared.ExternalLink href={link.url} Icon={FileTextIcon} title={link.url}>
                {link.label || link.url}
              </DynamicShared.ExternalLink>
              <Tooltip title={lockedLabel}>
                <Box
                  component="span"
                  tabIndex={0}
                  role="img"
                  aria-label={lockedLabel}
                  sx={{ display: "inline-flex", ml: "auto", color: theme => theme.palette.text.secondary }}
                >
                  <LockIcon size={14} />
                </Box>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      }
    />
  );
};
